import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ApiError } from '../../lib/api';
import {
  operatorConsole,
  type PoolShippingDetail,
  type ShippingCard,
} from '../../lib/api/operatorConsole';
import { Button } from '../../components/ui/Button';
import { ShippingCardsTable } from './ShippingCardsTable';
import { ShippingFacts } from './ShippingFacts';
import { ShippingFeesPanel } from './ShippingFeesPanel';
import { SandboxPanel } from './sandbox';

// A 404 here is not only "no such delivery": another operator's id answers the
// same way on purpose. Saying "not found" covers both without hinting which.
const NOT_FOUND = 'This delivery does not exist, or it is not one of yours.';

export function ShippingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const shippingId = Number(id);

  const [shipping, setShipping] = useState<PoolShippingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Reporting is money moving, so the row that is mid-flight is visible and no
  // second card can be reported until it lands.
  const [reportingCardId, setReportingCardId] = useState<number | null>(null);
  const [reportProblem, setReportProblem] = useState<string | null>(null);
  const [reportDone, setReportDone] = useState<string | null>(null);
  // Staging sandbox: while one of its actions runs, no card can be reported —
  // the two would race on the same line.
  const [sandboxBusy, setSandboxBusy] = useState(false);
  // Remounting the panel after a card report drops its last message, which no
  // longer describes the delivery.
  const [sandboxKey, setSandboxKey] = useState(0);

  useEffect(() => {
    if (!Number.isInteger(shippingId) || shippingId <= 0) {
      setLoading(false);
      setError(NOT_FOUND);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    operatorConsole
      .getShipping(shippingId)
      .then((loaded) => {
        if (!cancelled) setShipping(loaded);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError && err.status === 404
            ? NOT_FOUND
            : err instanceof Error
              ? err.message
              : 'Unknown error',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [shippingId]);

  function cardLabel(card: ShippingCard): string {
    return card.name ?? card.certificate_number ?? `card #${card.pool_item_id}`;
  }

  // Not reversible and it moves money, so the consequence is spelled out
  // instead of a bare "are you sure".
  async function handleReportUnavailable(card: ShippingCard) {
    if (sandboxBusy) return;

    const confirmed = window.confirm(
      `Report ${cardLabel(card)} as impossible to ship? The player is paid 100% of the higher of the card's market value or the pack price, and its handling fee (plus its share of any sales tax) is refunded. This cannot be undone.`,
    );
    if (!confirmed) return;

    setReportProblem(null);
    setReportDone(null);
    setReportingCardId(card.id);
    try {
      setShipping(await operatorConsole.reportCardUnavailable(shippingId, card.id));
      setReportDone(`${cardLabel(card)} reported as unavailable — the player is being paid back.`);
      setSandboxKey((key) => key + 1);
    } catch (err) {
      // The backend words the refusals ("already shipped", "a delivered
      // delivery can no longer be changed"), so they are quoted as they came.
      setReportProblem(
        err instanceof ApiError
          ? (err.message ?? 'The delivery did not accept this report.')
          : 'Could not reach the registry. Try again.',
      );
    } finally {
      setReportingCardId(null);
    }
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-4">
        <Button onClick={() => navigate('/shippings')} className="px-3 py-1 text-sm">
          ← Back
        </Button>
        <h1 className="text-2xl font-bold">
          Delivery {Number.isInteger(shippingId) && shippingId > 0 ? `#${shippingId}` : ''}
        </h1>
      </div>

      {loading ? (
        <div className="rounded-lg border border-ui-border bg-ui-card p-6 text-center text-ui-text-secondary">
          Loading delivery...
        </div>
      ) : error ? (
        <div
          role="alert"
          className="rounded-lg border border-ui-border bg-ui-card p-6 text-center text-[var(--co-error)]"
        >
          {error}
        </div>
      ) : shipping ? (
        <>
          {reportProblem && (
            <div
              role="alert"
              className="rounded-lg border border-ui-border bg-ui-card p-4 text-sm text-[var(--co-error)]"
            >
              {reportProblem}
            </div>
          )}
          {reportDone && (
            <div
              role="status"
              className="rounded-lg border border-ui-border bg-ui-card p-4 text-sm text-[var(--co-success)]"
            >
              {reportDone}
            </div>
          )}

          <ShippingFacts shipping={shipping} />
          {shipping.sandbox && (
            <SandboxPanel
              key={sandboxKey}
              shipping={shipping}
              sandbox={shipping.sandbox}
              disabled={reportingCardId !== null}
              onBusyChange={setSandboxBusy}
              onUpdated={(updated) => {
                setShipping(updated);
                setReportProblem(null);
                setReportDone(null);
              }}
            />
          )}
          <ShippingFeesPanel shipping={shipping} />
          <ShippingCardsTable
            cards={shipping.cards}
            currency={shipping.currency}
            onReportUnavailable={(card) => void handleReportUnavailable(card)}
            reportingCardId={reportingCardId}
            actionsDisabled={sandboxBusy}
          />
        </>
      ) : null}
    </div>
  );
}
